import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useBRollUpload } from './BRollUploadContext';
import CloudIcon from '@src/svgs/CloudIcon';

const MAX_FILE_SIZE_MB = 400;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const BRollUploadDropzone = observer(() => {
  const { bRollService, hasUploadingAssets } = useBRollUpload();
  const videoCreator = useVideoCreatorStore();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) return;

      const oversizedFiles = acceptedFiles.filter(
        (file) => file.size > MAX_FILE_SIZE_BYTES,
      );

      if (oversizedFiles.length > 0) {
        videoCreator.toastState = {
          state: 'error',
          message: `File size exceeds ${MAX_FILE_SIZE_MB}MB limit`,
        };

        const validFiles = acceptedFiles.filter(
          (file) => file.size <= MAX_FILE_SIZE_BYTES,
        );
        if (validFiles.length > 0) {
          bRollService.addPendingAssets(validFiles);
        }
      } else {
        bRollService.addPendingAssets(acceptedFiles);
      }
    },
    [bRollService, videoCreator],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
    },
    disabled: hasUploadingAssets,
    multiple: true,
  });

  return (
    <Container>
      <DropzoneArea
        {...getRootProps()}
        isDragActive={isDragActive}
        disabled={hasUploadingAssets}
      >
        <input {...getInputProps()} />
        <IconWrapper>
          <CloudIcon />
        </IconWrapper>
        <Text>
          Add your photos and videos
          <br />
          or drag and drop
          <br />
          <FileLimit>Max file size: {MAX_FILE_SIZE_MB}MB</FileLimit>
        </Text>
      </DropzoneArea>
    </Container>
  );
});

const Container = styled.div`
  width: 100%;
  height: 100%;
  padding-top: 44px;
`;

const DropzoneArea = styled.div<{ isDragActive: boolean; disabled: boolean }>`
  width: 100%;
  height: 144px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => (props.isDragActive ? '#5A5A5A' : '#484848')};
  border-radius: 8px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.2s ease;
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};

  &:hover {
    background-color: ${(props) => (props.disabled ? '#484848' : '#5A5A5A')};
  }
`;

const IconWrapper = styled.div`
  margin-bottom: 8px;
  color: #f3e9d7;
`;

const Text = styled.div`
  color: #f3e9d7;
  font-size: 12px;
  text-align: center;
  line-height: 1.4;
`;

const FileLimit = styled.div`
  color: #f2d093;
  font-size: 10px;
  margin-top: 8px;
`;

export default BRollUploadDropzone;
